import React, { useEffect, useMemo } from 'react';
import { ArrowBackIosNew, ArrowForwardIos, ReceiptLong } from '@mui/icons-material';
import { Box, Button, Chip, Grid, Paper, Stack, Typography } from '@mui/material';
import PaymentInvoiceList from './PaymentInvoiceList';
import PaymentInvoiceDetailPanel from './PaymentInvoiceDetailPanel';
import { buildUnifiedPaymentRows } from './paymentSummaryRows';
import { creditAllocationAmount } from '../../utils/settlementCredits';

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const roundMoney = (value) => Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

const buildDefaultUiState = (current = {}, fallbackInvoiceId = null) => ({
  activeInvoiceId:
    current?.activeInvoiceId != null
      ? Number(current.activeInvoiceId)
      : (current?.expandedInvoiceId != null ? Number(current.expandedInvoiceId) : fallbackInvoiceId),
  detailQuery: String(current?.detailQuery || ''),
  detailStatus: String(current?.detailStatus || 'all'),
  detailPage: Number(current?.detailPage || 0),
  detailPageSize: Number(current?.detailPageSize || 25),
});

export default function PaymentSummaryStep({
  selectedInvoices = [],
  supplierName = '',
  selectedDetails = [],
  selectedBonusDetails = [],
  detailsByInvoice = {},
  shortageSuggestions = [],
  priceDiffSuggestions = [],
  bonusSuggestions = [],
  earlyPaymentSuggestions = [],
  adjustmentsPayload = [],
  creditNotes = [],
  payableByInvoice = {},
  formatCurrency,
  summaryUiState,
  onSummaryUiStateChange,
  onBack,
  onContinue,
  disableContinue = false,
}) {
  const fallbackInvoiceId = selectedInvoices?.[0]?.id != null ? Number(selectedInvoices[0].id) : null;
  const uiState = useMemo(
    () => buildDefaultUiState(summaryUiState, fallbackInvoiceId),
    [summaryUiState, fallbackInvoiceId]
  );

  const rowsByInvoice = useMemo(
    () => buildUnifiedPaymentRows(selectedDetails, selectedBonusDetails),
    [selectedDetails, selectedBonusDetails]
  );

  const promotionalProductIdsByInvoice = useMemo(() => {
    const map = {};
    Object.entries(detailsByInvoice || {}).forEach(([invoiceId, pack]) => {
      const ids = new Set();
      const addFromBonus = (bonus) => {
        const productId =
          Number(bonus?.base_product_id)
          || Number(bonus?.product_id)
          || Number(bonus?.product?.id)
          || Number(bonus?.order_bonus?.base_product_id)
          || 0;
        if (productId) ids.add(productId);
      };
      (pack?.bonuses || pack?.header?.invoice_bonus || []).forEach(addFromBonus);
      (pack?.bonus_pending || pack?.header?.bonus_pending || []).forEach(addFromBonus);
      map[Number(invoiceId)] = ids;
    });
    return map;
  }, [detailsByInvoice]);

  const shortageByInvoice = useMemo(
    () => shortageSuggestions.reduce((acc, item) => {
      const invoiceId = Number(item.invoiceId);
      if (!invoiceId) return acc;
      acc[invoiceId] = roundMoney(
        (acc[invoiceId] || 0) + creditAllocationAmount(item)
      );
      return acc;
    }, {}),
    [shortageSuggestions]
  );

  const suggestedAdjustmentsByInvoice = useMemo(() => {
    const all = [...shortageSuggestions, ...priceDiffSuggestions, ...bonusSuggestions, ...earlyPaymentSuggestions];
    return all.reduce((acc, item) => {
      const invoiceId = Number(item.invoiceId);
      if (!invoiceId) return acc;
      acc[invoiceId] = roundMoney((acc[invoiceId] || 0) + Number(item.amount || 0));
      return acc;
    }, {});
  }, [shortageSuggestions, priceDiffSuggestions, bonusSuggestions, earlyPaymentSuggestions]);

  const appliedAdjustmentsByInvoice = useMemo(
    () => adjustmentsPayload.reduce((acc, item) => {
      const invoiceId = Number(item.invoice_id);
      if (!invoiceId) return acc;
      acc[invoiceId] = roundMoney((acc[invoiceId] || 0) + Number(item.amount || 0));
      return acc;
    }, {}),
    [adjustmentsPayload]
  );

  const creditNotesByInvoice = useMemo(
    () => creditNotes.reduce((acc, item) => {
      const invoiceId = Number(item.invoiceId);
      if (!invoiceId) return acc;
      acc[invoiceId] = roundMoney((acc[invoiceId] || 0) + Number(item.amount || 0));
      return acc;
    }, {}),
    [creditNotes]
  );

  const invoiceList = useMemo(() => selectedInvoices.map((invoice) => {
    const invoiceId = Number(invoice.id);
    const hasShortage = (shortageByInvoice[invoiceId] || 0) > 0;
    const hasPriceDiff = Boolean(invoice.hasPriceDiff) || Number(invoice.priceDiffTotalNum || 0) > 0;
    const hasPromo = (promotionalProductIdsByInvoice[invoiceId] || new Set()).size > 0;
    const hasCredit = (creditNotesByInvoice[invoiceId] || 0) > 0 || Boolean(invoice.hasPendingCreditNote);
    const hasPreviousPayment = Number(invoice.abonoNum || 0) > 0;

    return {
      id: invoice.id,
      number: invoice.number,
      series: invoice.series,
      supplier: supplierName || invoice.supplier,
      originalBalance: Number(invoice.totalOriginalNum ?? invoice.totalNum ?? 0),
      decimalAdjustment: Number(invoice.pendingAdjustmentAmount || 0),
      previousPayments: Number(invoice.abonoNum || 0),
      creditNotes: Number(creditNotesByInvoice[invoiceId] || 0),
      suggestedAdjustments: Number(suggestedAdjustmentsByInvoice[invoiceId] || 0),
      appliedAdjustments: Number(appliedAdjustmentsByInvoice[invoiceId] || 0),
      finalPayable: Number(payableByInvoice[invoiceId] ?? invoice.totalNum ?? 0),
      alerts: {
        shortage: hasShortage,
        priceDiff: hasPriceDiff,
        promotional: hasPromo,
        credit: hasCredit,
        previousPayment: hasPreviousPayment,
        review: hasShortage || hasPriceDiff || hasPromo || hasCredit,
      },
    };
  }), [
    selectedInvoices,
    supplierName,
    shortageByInvoice,
    promotionalProductIdsByInvoice,
    creditNotesByInvoice,
    suggestedAdjustmentsByInvoice,
    appliedAdjustmentsByInvoice,
    payableByInvoice,
  ]);

  const activeInvoiceId = useMemo(() => {
    const exists = invoiceList.some((item) => Number(item.id) === Number(uiState.activeInvoiceId));
    if (exists) return Number(uiState.activeInvoiceId);
    return invoiceList.length ? Number(invoiceList[0].id) : null;
  }, [invoiceList, uiState.activeInvoiceId]);

  useEffect(() => {
    if (!invoiceList.length) return;
    if (Number(uiState.activeInvoiceId) === Number(activeInvoiceId)) return;
    onSummaryUiStateChange?.((prev) => {
      const normalized = buildDefaultUiState(prev, fallbackInvoiceId);
      return {
        ...normalized,
        activeInvoiceId,
        detailPage: 0,
      };
    });
  }, [invoiceList.length, uiState.activeInvoiceId, activeInvoiceId, onSummaryUiStateChange, fallbackInvoiceId]);

  const activeInvoice = useMemo(
    () => invoiceList.find((item) => Number(item.id) === Number(activeInvoiceId)) || null,
    [invoiceList, activeInvoiceId]
  );

  const filteredDetailRows = useMemo(() => {
    if (!activeInvoiceId) return [];
    const invoiceId = Number(activeInvoiceId);
    const baseRows = rowsByInvoice[invoiceId] || [];
    const queryTokens = normalizeText(uiState.detailQuery).split(/\s+/).filter(Boolean);
    const selectedStatus = String(uiState.detailStatus || 'all');

    const promotionalIds = promotionalProductIdsByInvoice[invoiceId] || new Set();
    const invoiceHasDiscount = (creditNotesByInvoice[invoiceId] || 0) > 0 || (appliedAdjustmentsByInvoice[invoiceId] || 0) > 0;

    const withFlags = baseRows.map((row) => {
      if (row.rowType === 'bonus') {
        const pending = Number(row.pending ?? row.difference ?? 0);
        return {
          ...row,
          difference: pending,
          searchable: normalizeText(
            `${row.code} ${row.description} ${row.prformat} ${row.bonusKind} ${row.claimStatus} promo bonus`
          ),
          statusFlags: {
            ok: pending <= 0,
            shortage: false,
            priceDiff: false,
            promotional: true,
            discount: false,
            review: pending > 0,
          },
        };
      }
      const shortage = Number(row.quantityRe || 0) < Number(row.quantity || 0);
      const priceDiff = Math.abs(Number(row.priceDiff || 0)) >= 0.01;
      const promotional = promotionalIds.has(Number(row.productId || 0));
      const review = shortage || priceDiff || promotional;
      const discount = invoiceHasDiscount;
      const ok = !shortage && !priceDiff && !promotional && !discount;
      const searchable = normalizeText(`${row.code} ${row.description} ${row.prformat}`);

      return {
        ...row,
        difference: roundMoney(Number(row.quantity || 0) - Number(row.quantityRe || 0)),
        searchable,
        statusFlags: { ok, shortage, priceDiff, promotional, discount, review },
      };
    });

    return withFlags.filter((row) => {
      const queryMatches = queryTokens.length === 0
        || queryTokens.every((token) => row.searchable.includes(token));
      if (!queryMatches) return false;
      if (selectedStatus === 'all') return true;
      return Boolean(row.statusFlags?.[selectedStatus]);
    });
  }, [
    activeInvoiceId,
    rowsByInvoice,
    uiState.detailQuery,
    uiState.detailStatus,
    promotionalProductIdsByInvoice,
    creditNotesByInvoice,
    appliedAdjustmentsByInvoice,
  ]);

  if (selectedInvoices.length === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
        <ReceiptLong sx={{ fontSize: 34, color: 'action.disabled', mb: 1 }} />
        <Typography variant="body2" color="text.secondary">
          No hay facturas seleccionadas
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Resumen operativo
        </Typography>
        <Chip
          size="small"
          label={`${selectedInvoices.length} ${selectedInvoices.length === 1 ? 'factura' : 'facturas'}`}
          color="primary"
          variant="outlined"
        />
      </Stack>

      <Grid container spacing={1.5}>
        <Grid item xs={12} md={4}>
          <PaymentInvoiceList
            invoices={invoiceList}
            activeInvoiceId={activeInvoiceId}
            onSelect={(invoiceId) => {
              onSummaryUiStateChange?.((prev) => {
                const normalized = buildDefaultUiState(prev, fallbackInvoiceId);
                return {
                  ...normalized,
                  activeInvoiceId: Number(invoiceId),
                  detailPage: 0,
                };
              });
            }}
            formatCurrency={formatCurrency}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <PaymentInvoiceDetailPanel
            invoice={activeInvoice}
            metrics={activeInvoice}
            alerts={activeInvoice?.alerts}
            rows={filteredDetailRows}
            query={uiState.detailQuery}
            status={uiState.detailStatus}
            onQueryChange={(value) => {
              onSummaryUiStateChange?.((prev) => {
                const normalized = buildDefaultUiState(prev, fallbackInvoiceId);
                return {
                  ...normalized,
                  detailQuery: value,
                  detailPage: 0,
                };
              });
            }}
            onStatusChange={(value) => {
              onSummaryUiStateChange?.((prev) => {
                const normalized = buildDefaultUiState(prev, fallbackInvoiceId);
                return {
                  ...normalized,
                  detailStatus: value,
                  detailPage: 0,
                };
              });
            }}
            paginationModel={{ page: uiState.detailPage, pageSize: uiState.detailPageSize }}
            onPaginationModelChange={(model) => {
              onSummaryUiStateChange?.((prev) => {
                const normalized = buildDefaultUiState(prev, fallbackInvoiceId);
                return {
                  ...normalized,
                  detailPage: Number(model?.page || 0),
                  detailPageSize: Number(model?.pageSize || 25),
                };
              });
            }}
            formatCurrency={formatCurrency}
          />
        </Grid>
      </Grid>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          pt: 2.25,
          mt: 1.25,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1.25, sm: 0 },
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          size="small"
          onClick={onBack}
          startIcon={<ArrowBackIosNew sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            px: 2.5,
            fontSize: '0.8rem',
            height: 36,
            order: { xs: 2, sm: 1 },
          }}
        >
          Regresar
        </Button>

        <Button
          variant="contained"
          color="primary"
          onClick={onContinue}
          size="small"
          sx={{
            textTransform: 'none',
            borderRadius: 1.5,
            px: 3,
            fontSize: '0.85rem',
            height: 36,
            fontWeight: 500,
            order: { xs: 1, sm: 2 },
          }}
          endIcon={<ArrowForwardIos sx={{ fontSize: 14 }} />}
          disabled={disableContinue}
        >
          Continuar al pago
        </Button>
      </Box>
    </Box>
  );
}
